import { useState } from 'react';
import { CreditCard } from 'lucide-react';
import { LIMITS, P, type LimitKey } from '@stockpilot/shared';
import { useAccess, useFormat } from '@/hooks/useAccess';
import { usePlansQuery, useRenewSubscriptionMutation, useSubscriptionQuery } from '@/api/endpoints';
import { apiErrorMessage } from '@/api/baseApi';
import { formatDate, formatNumber } from '@/lib/format';
import {
  Alert, Badge, Button, Card, CardBody, CardHeader, ErrorState, Input, LoadingState,
  PageHeader, Select,
} from '@/components/ui';
import { Can } from '@/components/Can';
import { isoDate } from '@/lib/format';

const LIMIT_LABELS: Record<LimitKey, string> = {
  [LIMITS.USERS]: 'Team members',
  [LIMITS.BRANCHES]: 'Branches',
  [LIMITS.WAREHOUSES]: 'Warehouses',
  [LIMITS.PRODUCTS]: 'Products',
  [LIMITS.MONTHLY_TRANSACTIONS]: 'Transactions this month',
  [LIMITS.STORAGE_BYTES]: 'Storage used',
};

export function SubscriptionPage() {
  const format = useFormat();
  const { isOwner } = useAccess();
  const { data, isLoading, isError, error, refetch } = useSubscriptionQuery();
  const plans = usePlansQuery();
  const [renew, renewState] = useRenewSubscriptionMutation();

  const [renewal, setRenewal] = useState({ paidUntil: '', planCode: '', billingCycle: 'MONTHLY' });
  const [renewed, setRenewed] = useState<string | null>(null);
  const [failure, setFailure] = useState<string | null>(null);

  const submitRenewal = async (): Promise<void> => {
    setFailure(null);
    setRenewed(null);
    try {
      await renew({
        paidUntil: renewal.paidUntil,
        ...(renewal.planCode ? { planCode: renewal.planCode } : {}),
        billingCycle: renewal.billingCycle,
      }).unwrap();
      setRenewed(`Recorded as paid until ${renewal.paidUntil}.`);
    } catch (renewError) {
      setFailure(apiErrorMessage(renewError, 'That renewal could not be recorded'));
    }
  };

  if (isLoading) return <LoadingState label="Loading your subscription…" />;
  if (isError) return <ErrorState message={apiErrorMessage(error)} onRetry={() => void refetch()} />;
  if (!data) return null;

  const trialing = data.status === 'TRIALING';

  // The same test the API makes: a trial that ran out, a cancelled or expired
  // subscription, or a period whose end has passed.
  const periodEnd = data.currentPeriodEnd ? new Date(data.currentPeriodEnd) : null;
  const trialEnd = data.trialEndsAt ? new Date(data.trialEndsAt) : null;
  const lapsed =
    data.status === 'CANCELLED' ||
    data.status === 'EXPIRED' ||
    (trialing ? Boolean(trialEnd && trialEnd.getTime() < Date.now())
              : Boolean(periodEnd && periodEnd.getTime() < Date.now()));

  return (
    <>
      <PageHeader title="Subscription" description="Your plan, what it includes and how much of it you are using." />

      {lapsed && (
        <div className="mb-4">
          <Alert tone="danger" title="This subscription has lapsed">
            The business is <strong>read-only</strong> for everyone except you: people can look
            things up, but no sales, payments or stock movements can be recorded until it is
            renewed below.
          </Alert>
        </div>
      )}

      {failure && <div className="mb-4"><Alert tone="danger">{failure}</Alert></div>}
      {renewed && <div className="mb-4"><Alert tone="success">{renewed}</Alert></div>}

      {trialing && data.trialEndsAt && (
        <div className="mb-4">
          <Alert tone="info" title="You are on a trial">
            Your trial of {data.plan.name} runs until {formatDate(data.trialEndsAt, format)}.
          </Alert>
        </div>
      )}

      <div className="grid gap-4 lg:grid-cols-3">
        <Card>
          <CardHeader title="Current plan" />
          <CardBody className="space-y-3">
            <div className="flex items-center gap-2">
              <CreditCard className="size-5 text-brand-600" aria-hidden />
              <span className="text-xl font-semibold">{data.plan.name}</span>
              <Badge tone={trialing ? 'info' : data.status === 'ACTIVE' ? 'success' : 'warning'}>
                {data.status.toLowerCase()}
              </Badge>
            </div>
            <p className="text-sm text-secondary">
              {Number.parseFloat(data.plan.monthlyPrice) === 0
                ? 'Free'
                : `${data.plan.currency} ${data.plan.monthlyPrice} per month`}
            </p>
            {data.currentPeriodEnd && (
              <p className="text-sm text-muted">
                Current period ends {formatDate(data.currentPeriodEnd, format)}
              </p>
            )}
          </CardBody>
        </Card>

        <Card className="lg:col-span-2">
          <CardHeader title="Usage" description="Measured against your plan's limits." />
          <CardBody className="space-y-4">
            {(Object.entries(data.usage) as [LimitKey, { used: number; max: number | null }][]).map(
              ([key, usage]) => {
                const isStorage = key === LIMITS.STORAGE_BYTES;
                const used = isStorage ? usage.used / (1024 * 1024) : usage.used;
                const max = usage.max === null ? null : isStorage ? usage.max / (1024 * 1024) : usage.max;
                const percent = max ? Math.min(100, Math.round((used / max) * 100)) : 0;

                return (
                  <div key={key}>
                    <div className="mb-1 flex items-center justify-between text-sm">
                      <span>{LIMIT_LABELS[key] ?? key}</span>
                      <span className="tabular text-secondary">
                        {isStorage ? `${used.toFixed(1)} MB` : formatNumber(used, format.locale)}
                        {max === null
                          ? ' / unlimited'
                          : ` / ${isStorage ? `${max.toFixed(0)} MB` : formatNumber(max, format.locale)}`}
                      </span>
                    </div>
                    <div className="h-2 overflow-hidden rounded-full bg-sunken">
                      <div
                        className={
                          percent >= 90
                            ? 'h-full rounded-full bg-red-500'
                            : percent >= 70
                              ? 'h-full rounded-full bg-amber-500'
                              : 'h-full rounded-full bg-brand-500'
                        }
                        style={{ width: max === null ? '0%' : `${percent}%` }}
                        role="progressbar"
                        aria-valuenow={percent}
                        aria-valuemin={0}
                        aria-valuemax={100}
                        aria-label={LIMIT_LABELS[key] ?? key}
                      />
                    </div>
                  </div>
                );
              },
            )}
          </CardBody>
        </Card>

        <Card className="lg:col-span-3">
          <CardHeader title="Included modules" />
          <CardBody>
            <div className="flex flex-wrap gap-2">
              {data.features.map((feature) => (
                <Badge key={feature} tone="brand">
                  {feature.toLowerCase().replaceAll('_', ' ')}
                </Badge>
              ))}
            </div>
          </CardBody>
        </Card>
      </div>

      <Can permission={P.SUBSCRIPTION_MANAGE}>
        <Card className="mt-4">
          <CardHeader
            title="Renew"
            description="Record the business as paid up to a date. Only you can do this, and you are never locked out of it."
          />
          <CardBody className="space-y-4">
            {!isOwner && (
              <Alert tone="info">
                Renewing is the owner&rsquo;s to do — they are the one who can always reach this
                screen, whatever state the subscription is in.
              </Alert>
            )}

            <div className="grid gap-4 sm:grid-cols-3">
              <Input
                label="Paid until"
                type="date"
                required
                min={isoDate()}
                value={renewal.paidUntil}
                onChange={(event) => setRenewal({ ...renewal, paidUntil: event.target.value })}
                hint="The day the business is paid up to, inclusive."
              />
              <Select
                label="Plan"
                value={renewal.planCode}
                onChange={(event) => setRenewal({ ...renewal, planCode: event.target.value })}
                hint="Blank keeps the current plan."
              >
                <option value="">{data.plan.name} (no change)</option>
                {plans.data?.map((plan) => (
                  <option key={plan.code} value={plan.code}>{plan.name}</option>
                ))}
              </Select>
              <Select
                label="Billed"
                value={renewal.billingCycle}
                onChange={(event) => setRenewal({ ...renewal, billingCycle: event.target.value })}
              >
                <option value="MONTHLY">Monthly</option>
                <option value="YEARLY">Yearly</option>
              </Select>
            </div>

            <div className="flex justify-end">
              <Button
                loading={renewState.isLoading}
                disabled={!renewal.paidUntil}
                onClick={() => void submitRenewal()}
              >
                Record the renewal
              </Button>
            </div>
          </CardBody>
        </Card>
      </Can>
    </>
  );
}
